import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { STORIES } from "../lib/storiesData";

const Header = () => (
  <header className="fixed top-0 inset-x-0 z-50 bg-[#0a0a0a]/80 backdrop-blur-md border-b border-white/10">
    <div className="mx-auto max-w-[1500px] w-full px-5 sm:px-8 h-16 flex items-center justify-between">
      <Link to="/" data-testid="site-logo" className="shrink-0">
        <img
          src="/owos-logo-badge.png"
          alt="One World of Sports"
          className="h-11 w-auto object-contain"
        />
      </Link>
      <Link
        to="/#gateways"
        data-testid="stories-back-explore-top"
        className="group inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] font-bold text-neutral-400 hover:text-white transition-colors"
      >
        <ArrowLeft className="h-4 w-4 group-hover:-translate-x-0.5 transition-transform" />
        Explore
      </Link>
    </div>
  </header>
);

export default function Stories() {
  useEffect(() => {
    document.title = "Stories — One World of Sports";
  }, []);

  return (
    <div data-testid="stories-page" className="bg-[#0a0a0a] min-h-screen text-white">
      <Header />

      <section className="mx-auto max-w-[1500px] px-5 sm:px-8 pt-36 sm:pt-44 pb-14 sm:pb-20">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        >
          <p className="flex items-center gap-3 text-xs font-bold uppercase tracking-[0.3em] text-[#e62429]">
            <span className="h-px w-10 bg-[#e62429]" />
            Explore · Stories
          </p>
          <h1 className="mt-6 font-display uppercase text-white text-[22vw] sm:text-[16vw] lg:text-[12rem] leading-[0.82]">
            Stories
          </h1>
        </motion.div>
      </section>

      {/* Shelf — one story today, built to hold more */}
      <section
        data-testid="stories-shelf"
        className="mx-auto max-w-[1500px] px-5 sm:px-8 pb-28 sm:pb-40"
      >
        <div className="border-t border-white/12">
          {STORIES.map((s, i) => (
            <motion.div
              key={s.slug}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            >
              <Link
                to={`/stories/${s.slug}`}
                data-testid={`story-card-${s.slug}`}
                className="group grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 items-center py-10 sm:py-14 border-b border-white/12"
              >
                <div className="lg:col-span-7 order-2 lg:order-1">
                  <div className="flex items-baseline gap-5">
                    <span className="font-display text-2xl sm:text-3xl text-neutral-600 group-hover:text-[#e62429] transition-colors">
                      {s.num}
                    </span>
                    <h2 className="font-display uppercase leading-[0.9] text-5xl sm:text-6xl lg:text-7xl text-white">
                      {s.title}
                    </h2>
                  </div>
                  <div className="mt-5 pl-[3.4rem] flex items-center gap-4">
                    <span className="text-xs uppercase tracking-[0.28em] font-bold text-neutral-400">
                      {s.locationDate}
                    </span>
                    <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.28em] font-bold text-white/70 group-hover:text-white transition-colors">
                      Read
                      <ArrowUpRight className="h-3.5 w-3.5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                    </span>
                  </div>
                </div>

                <div className="lg:col-span-5 order-1 lg:order-2 overflow-hidden">
                  <img
                    src={s.hero}
                    alt={`${s.title} — ${s.locationDate}`}
                    className="w-full h-[220px] sm:h-[300px] object-cover grayscale-[0.15] group-hover:grayscale-0 group-hover:scale-[1.03] transition-[transform,filter] duration-[900ms]"
                  />
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>
    </div>
  );
}
